import { useCallback, useEffect, useMemo, useState } from "react";
import { ACTIVE_YEAR } from "../constants.js";
import {
  colRefs,
  onSnapshot,
  query,
  where,
} from "../services/firestore.js";
import {
  aggregateFustMovements,
  buildFustOverviewRows,
  calculateOutstandingFustDemand,
  CRATE_TYPE_OPTIONS,
  getLowCrateStockAlerts,
  getLowPalletStockAlerts,
  isFustTypeHidden,
  OWN_PALLET_TYPE,
  PALLET_TYPE_OPTIONS,
} from "../utils/fustInventory.js";
import { logger } from "../utils/logger.js";
import { upgradedWorkflowEnabled } from "../config/runtimeFeatures.js";

const totalsAsMovements = (data = {}) => [
  ...Object.entries(data.crateStock || {}).map(([crateType, quantity]) => ({
    direction: Number(quantity) < 0 ? "out" : "in",
    crateType,
    crateQuantity: Math.abs(Number(quantity) || 0),
  })),
  ...Object.entries(data.palletStock || {}).map(([palletType, quantity]) => ({
    direction: Number(quantity) < 0 ? "out" : "in",
    palletType,
    palletQuantity: Math.abs(Number(quantity) || 0),
  })),
];

const EMPTY_LOAD_STATE = {
  movements: false,
  orders: false,
  pallets: false,
  settings: false,
};

function getDismissedStorageKey(userId) {
  return `kv_fust_alerts_dismissed_${userId || "unknown"}`;
}

function readDismissedTypes(userId) {
  try {
    const value = JSON.parse(localStorage.getItem(getDismissedStorageKey(userId)) || "[]");
    return Array.isArray(value) ? value : [];
  } catch {
    return [];
  }
}

function writeDismissedTypes(userId, types) {
  try {
    localStorage.setItem(getDismissedStorageKey(userId), JSON.stringify(types));
  } catch {
    // De waarschuwing blijft werken als lokale opslag niet beschikbaar is.
  }
}

export default function useFustAlerts({ enabled, userId }) {
  const [movements, setMovements] = useState([]);
  const [orders, setOrders] = useState([]);
  const [readyPallets, setReadyPallets] = useState([]);
  const [loaded, setLoaded] = useState(EMPTY_LOAD_STATE);
  const [loadFailed, setLoadFailed] = useState(false);
  const [dismissedTypes, setDismissedTypes] = useState([]);
  const [hiddenCrateTypes, setHiddenCrateTypes] = useState([]);
  const [hiddenPalletTypes, setHiddenPalletTypes] = useState([]);

  useEffect(() => {
    setDismissedTypes(readDismissedTypes(userId));
  }, [userId]);

  useEffect(() => {
    if (!enabled) {
      setMovements([]);
      setOrders([]);
      setReadyPallets([]);
      setHiddenCrateTypes([]);
      setHiddenPalletTypes([]);
      setLoaded(EMPTY_LOAD_STATE);
      setLoadFailed(false);
      return undefined;
    }

    const markLoaded = (part) => {
      setLoaded((current) => ({ ...current, [part]: true }));
    };
    const handleError = (label, part, error) => {
      logger.error(label, error);
      setLoadFailed(true);
      markLoaded(part);
    };

    const unsubscribeMovements = onSnapshot(
      upgradedWorkflowEnabled
        ? colRefs.fustTotalsDoc(ACTIVE_YEAR)
        : colRefs.fustMovements(ACTIVE_YEAR),
      (snapshot) => {
        setMovements(upgradedWorkflowEnabled
          ? totalsAsMovements(snapshot.exists() ? snapshot.data() : {})
          : snapshot.docs.map((document) => ({ id: document.id, ...document.data() })));
        markLoaded("movements");
      },
      (error) => handleError("Fustwaarschuwing: mutaties laden mislukt", "movements", error)
    );

    const ordersRef = upgradedWorkflowEnabled
      ? query(
          colRefs.weekOrders(),
          where("status", "in", ["planned", "printQueued", "printed"])
        )
      : colRefs.weekOrdersStateDoc();
    const unsubscribeOrders = onSnapshot(
      ordersRef,
      (snapshot) => {
        setOrders(upgradedWorkflowEnabled
          ? snapshot.docs.map((document) => ({ id: document.id, ...document.data() }))
          : Array.isArray(snapshot.data()?.orders) ? snapshot.data().orders : []);
        markLoaded("orders");
      },
      (error) => handleError("Fustwaarschuwing: orders laden mislukt", "orders", error)
    );

    const unsubscribePallets = onSnapshot(
      query(colRefs.pallets(ACTIVE_YEAR), where("status", "==", "ready")),
      (snapshot) => {
        setReadyPallets(snapshot.docs.map((document) => ({ id: document.id, ...document.data() })));
        markLoaded("pallets");
      },
      (error) => handleError("Fustwaarschuwing: pallets laden mislukt", "pallets", error)
    );

    const unsubscribeSettings = onSnapshot(
      colRefs.fustTypeSettingsDoc(ACTIVE_YEAR),
      (snapshot) => {
        const settings = snapshot.exists() ? snapshot.data() || {} : {};
        setHiddenCrateTypes(
          Array.isArray(settings.hiddenCrateTypes) ? settings.hiddenCrateTypes : []
        );
        setHiddenPalletTypes(
          Array.isArray(settings.hiddenPalletTypes) ? settings.hiddenPalletTypes : []
        );
        markLoaded("settings");
      },
      (error) => handleError(
        "Fustwaarschuwing: verborgen soorten laden mislukt",
        "settings",
        error
      )
    );

    return () => {
      unsubscribeMovements();
      unsubscribeOrders();
      unsubscribePallets();
      unsubscribeSettings();
    };
  }, [enabled]);

  const allDataLoaded = loaded.movements && loaded.orders && loaded.pallets && loaded.settings;

  const activeAlerts = useMemo(() => {
    if (!enabled || !allDataLoaded || loadFailed) return [];

    const stock = aggregateFustMovements(movements);
    const demand = calculateOutstandingFustDemand(orders, readyPallets);
    const crateRows = buildFustOverviewRows(
      stock.crateStock,
      demand.crateDemand,
      CRATE_TYPE_OPTIONS
    ).filter((row) => !isFustTypeHidden(row.type, hiddenCrateTypes));
    const palletRows = buildFustOverviewRows(
      stock.palletStock,
      demand.palletDemand,
      PALLET_TYPE_OPTIONS.filter((type) => type !== OWN_PALLET_TYPE)
    ).filter((row) => !isFustTypeHidden(row.type, hiddenPalletTypes));

    return [
      ...getLowCrateStockAlerts(crateRows).map((alert) => ({
        ...alert,
        alertKey: `crate:${alert.type}`,
        unit: "kisten",
      })),
      ...getLowPalletStockAlerts(palletRows).map((alert) => ({
        ...alert,
        alertKey: `pallet:${alert.type}`,
        unit: "pallets",
      })),
    ];
  }, [
    allDataLoaded,
    enabled,
    hiddenCrateTypes,
    hiddenPalletTypes,
    loadFailed,
    movements,
    orders,
    readyPallets,
  ]);

  useEffect(() => {
    if (!allDataLoaded || loadFailed) return;

    const activeTypes = new Set(activeAlerts.map((alert) => alert.alertKey));
    setDismissedTypes((current) => {
      const next = current.filter((type) => activeTypes.has(type));
      if (next.length === current.length && next.every((type, index) => type === current[index])) {
        return current;
      }
      writeDismissedTypes(userId, next);
      return next;
    });
  }, [activeAlerts, allDataLoaded, loadFailed, userId]);

  const visibleAlerts = useMemo(() => {
    const dismissed = new Set(dismissedTypes);
    return activeAlerts.filter((alert) => !dismissed.has(alert.alertKey));
  }, [activeAlerts, dismissedTypes]);

  const dismissAlerts = useCallback(() => {
    setDismissedTypes((current) => {
      const next = Array.from(new Set([
        ...current,
        ...visibleAlerts.map((alert) => alert.alertKey),
      ]));
      writeDismissedTypes(userId, next);
      return next;
    });
  }, [userId, visibleAlerts]);

  return { alerts: visibleAlerts, dismissAlerts };
}
